import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { useAvailableFeatures } from './useAvailableFeatures'
import { useMemo } from 'react'

export const useProductReordering = (props?: { phaseId?: string }) => {
  const cubeCtx = useCubeContext()
  const { availableFeatures } = useAvailableFeatures()
  const canEditQuote = availableFeatures.quote.edit.available.enabled
  const lockedForPhase = props?.phaseId
    ? !availableFeatures.phases[props.phaseId]?.phase.product.reorder.available
        .enabled
    : false

  const productOrder =
    cubeCtx.queries.rawData.data.presentation.settings
      ?.priceEditorProductOrder ?? []
  const allUniqueProductIds = useMemo(
    () =>
      Array.from(
        new Set([
          ...Object.values(cubeCtx.queries.rawData.data.prices).map(
            ({ productId }) => productId
          )
        ])
      ),
    [cubeCtx.queries.rawData.data.prices]
  )

  /**
   * By accounting for unordered products here, we have an
   * easier time migrating from having zero order defined (old quotes;
   * new products) to having an order defined, as we actively model
   * the 'unordered' productIds at the end of the array. Therefore,
   * when the new order is set, using this order, we commit those
   * missing values too!
   *
   * We can also do this default ordering in the API, as well, but
   * this approach also captures in-session changes neatly too, without
   * the need for a post action step/cleanup needing to kick in.
   */
  const productOrderIncludingUnordered = useMemo(() => {
    const unorderedProducts = allUniqueProductIds.filter(
      productId => !productOrder?.includes(productId)
    )

    return [...(productOrder ?? []), ...unorderedProducts]
  }, [allUniqueProductIds, productOrder])

  return {
    enabled: canEditQuote && !lockedForPhase,
    productOrder: productOrderIncludingUnordered,
    setProductOrder: (newOrder: Array<string>) => {
      cubeCtx.mutators.updateData({
        presentation: {
          settings: {
            /**
             * Ensure uniqueness to prevent duplicates
             */
            priceEditorProductOrder: [...new Set(newOrder)]
          }
        }
      })
    }
  }
}
